import type {
  CadastrarDistribuicaoBody,
  ColetorDoTipo,
  DistribuicaoColetor,
  ItemDistribuicao,
  ParteDistribuicao,
  ParteDoTipo,
} from "@/modules/financeiro-configuracoes";
import {
  NOME_MAX,
  NOME_MUITO_LONGO,
  NOME_OBRIGATORIO,
  PERCENTUAL_INVALIDO,
  SOMA_ALVO,
  TIPO_OBRIGATORIO,
} from "./distribuicao-dialog.constants";

export type Percentuais = Record<string, number | null>;

export type DistribuicaoForm = {
  nome: string;
  tipoObraId: string | null;
  percentuais: Percentuais;
};

export type ParteEditavel = Exclude<ParteDistribuicao, "FACILIDADES">;

export const PARTES_EDITAVEIS: readonly ParteEditavel[] = ["MATERIAIS", "SERVICOS"];

const centesimos = (valor: number) => Math.round(valor * 100);

export const folhas = (coletores: ColetorDoTipo[]): ColetorDoTipo[] =>
  coletores.flatMap((coletor) => (coletor.filhos.length > 0 ? folhas(coletor.filhos) : [coletor]));

export const coletoresDaParte = (
  partes: ParteDoTipo[],
  parte: ParteDistribuicao,
): ColetorDoTipo[] => partes.find((candidata) => candidata.parte === parte)?.coletores ?? [];

export function percentualDo(coletor: ColetorDoTipo, percentuais: Percentuais): number {
  if (coletor.filhos.length === 0) return percentuais[coletor.id] ?? 0;
  return (
    centesimos(
      coletor.filhos.reduce((total, filho) => total + percentualDo(filho, percentuais), 0),
    ) / 100
  );
}

export const somaDaParte = (coletores: ColetorDoTipo[], percentuais: Percentuais): number =>
  centesimos(coletores.reduce((total, coletor) => total + percentualDo(coletor, percentuais), 0)) /
  100;

export const somaValida = (soma: number): boolean =>
  Math.abs(centesimos(soma) - centesimos(SOMA_ALVO)) <= 1;

export const formatSoma = (soma: number): string =>
  new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 2 }).format(soma);

export const mensagemSoma = (soma: number): string =>
  `A soma está em ${formatSoma(soma)}%. Ajuste os percentuais até chegar a 100%.`;

export const erroPercentual = (valor: number | null): string | null =>
  valor !== null && (valor < 0 || valor > 100) ? PERCENTUAL_INVALIDO : null;

export function erroNome(nome: string): string | null {
  const aparado = nome.trim();
  if (aparado === "") return NOME_OBRIGATORIO;
  return aparado.length > NOME_MAX ? NOME_MUITO_LONGO : null;
}

export const erroTipo = (tipoObraId: string | null): string | null =>
  tipoObraId ? null : TIPO_OBRIGATORIO;

export const somasValidas = (partes: ParteDoTipo[], percentuais: Percentuais): boolean =>
  PARTES_EDITAVEIS.every((parte) =>
    somaValida(somaDaParte(coletoresDaParte(partes, parte), percentuais)),
  );

export const faixasValidas = (percentuais: Percentuais): boolean =>
  Object.values(percentuais).every((valor) => erroPercentual(valor) === null);

export const temPercentual = (percentuais: Percentuais): boolean =>
  Object.values(percentuais).some((valor) => valor !== null);

export function montarItens(partes: ParteDoTipo[], percentuais: Percentuais): ItemDistribuicao[] {
  return PARTES_EDITAVEIS.flatMap((parte) =>
    folhas(coletoresDaParte(partes, parte)).map((coletor) => ({
      coletorId: coletor.id,
      percentual: percentuais[coletor.id] ?? 0,
    })),
  );
}

export function montarCorpo(
  form: DistribuicaoForm,
  partes: ParteDoTipo[],
): CadastrarDistribuicaoBody {
  return {
    nome: form.nome.trim(),
    tipoObraId: form.tipoObraId ?? "",
    itens: montarItens(partes, form.percentuais),
  };
}

export function percentuaisDa(distribuicao: DistribuicaoColetor): Percentuais {
  const percentuais: Percentuais = {};
  const registrar = (coletores: DistribuicaoColetor["partes"][number]["coletores"]) => {
    for (const coletor of coletores) {
      if (coletor.filhos.length > 0) registrar(coletor.filhos);
      else percentuais[coletor.id] = coletor.percentual;
    }
  };
  for (const parte of distribuicao.partes) {
    if (parte.parte !== "FACILIDADES") registrar(parte.coletores);
  }
  return percentuais;
}

export function letrasDosPais(coletores: ColetorDoTipo[]): string {
  const letras = coletores
    .filter((coletor) => coletor.filhos.length > 0)
    .map((coletor) => coletor.codigo.trim().split(/\s+/).pop() ?? coletor.codigo);
  if (letras.length <= 1) return letras.join("");
  return `${letras.slice(0, -1).join(", ")} e ${letras[letras.length - 1]}`;
}
